import { useCallback, useEffect, useState } from "react";
import { Search, UserPlus, Crown, KeyRound, Trash2, Ban, ShieldCheck, ShieldOff, X, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fmtNumber } from "@/lib/format";

const ROLES = [
  { id: "user",     label: "Usuario" },
  { id: "creator",  label: "Creador" },
  { id: "business", label: "Empresa" },
  { id: "investor", label: "Inversor" },
  { id: "traveler", label: "Viajero" },
];

export default function RealUsersTab() {
  const auth = useAdminAuth();
  const [data, setData] = useState({ total: 0, items: [] });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [skip, setSkip] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [credToShow, setCredToShow] = useState(null); // {email, password, mode}
  const limit = 20;

  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    if (q) params.set("q", q);
    if (role) params.set("role", role);
    auth.apiGet(`/admin/users-real?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudieron cargar los usuarios"));
  }, [auth, q, role, skip]);

  useEffect(load, [load]);

  const ban = async (u) => {
    try { await auth.apiPost(`/admin/users-real/${u.id}/ban`, {}); toast.success("Usuario suspendido"); load(); }
    catch { toast.error("Error"); }
  };
  const unban = async (u) => {
    try { await auth.apiPost(`/admin/users-real/${u.id}/unban`, {}); toast.success("Usuario reactivado"); load(); }
    catch { toast.error("Error"); }
  };
  const deact = async (u) => {
    try { await auth.apiPost(`/admin/users-real/${u.id}/deactivate`, {}); toast.success("Usuario desactivado"); load(); }
    catch { toast.error("Error"); }
  };
  const act = async (u) => {
    try { await auth.apiPost(`/admin/users-real/${u.id}/activate`, {}); toast.success("Usuario activado"); load(); }
    catch { toast.error("Error"); }
  };
  const resetPw = async (u) => {
    if (!window.confirm(`Generar nueva contraseña para ${u.email}? El usuario actual no podrá entrar con la antigua.`)) return;
    try {
      const { data: res } = await auth.apiPost(`/admin/users-real/${u.id}/reset-password`, {});
      setCredToShow({ email: u.email, password: res.new_password, mode: "reset" });
    } catch { toast.error("Error"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Eliminar definitivamente a ${u.email}? Esta acción NO se puede deshacer.`)) return;
    try { await auth.apiDelete(`/admin/users-real/${u.id}`); toast.success("Usuario eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div data-testid="tab-users-real" className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">
          ◊ Usuarios reales · {fmtNumber(data.total)} totales
        </p>
        <button
          data-testid="users-real-create-btn"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-ink text-cream hover:bg-coral transition"
        >
          <UserPlus className="w-3.5 h-3.5" /> Crear usuario
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
          <input
            data-testid="users-real-search"
            placeholder="Buscar por nombre, email, handle o id…"
            value={q}
            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            className="w-full bg-cream-card border hairline rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:border-coral"
          />
        </div>
        <select
          data-testid="users-real-role-filter"
          value={role}
          onChange={(e) => { setSkip(0); setRole(e.target.value); }}
          className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none"
        >
          <option value="">Todos los roles</option>
          {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
      </div>

      <section className="border hairline bg-cream-card rounded-2xl p-3 md:p-5">
        {data.items.length === 0 ? (
          <p className="text-sm text-ink-mid text-center py-10" data-testid="users-real-empty">
            Aún no hay usuarios reales registrados. Crea el primero o invita a tu equipo.
          </p>
        ) : data.items.map((u) => {
          const avatar = u.avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${encodeURIComponent(u.email)}`;
          return (
            <div key={u.id} data-testid={`users-real-row-${u.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 border-b hairline last:border-b-0 items-center">
              <div className="col-span-12 md:col-span-4 flex items-center gap-3 min-w-0">
                <img src={avatar} alt="" className="w-9 h-9 rounded-full bg-cream-deep" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    {u.is_pro && <Crown className="w-3 h-3 text-coral" />}
                    {u.is_verified && <ShieldCheck className="w-3 h-3 text-emerald-600" />}
                  </div>
                  <p className="text-[11px] text-ink-mid font-mono truncate">{u.email}</p>
                </div>
              </div>
              <div className="col-span-4 md:col-span-2 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-coral/10 text-coral font-mono uppercase tracking-wider text-[10px]">{u.role}</span>
              </div>
              <div className="col-span-4 md:col-span-3 flex flex-wrap gap-1 text-[9px] font-mono">
                {!u.is_active && <span className="px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-700 uppercase">Inactivo</span>}
                {u.is_banned && <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 uppercase">Suspendido</span>}
                {u.is_active && !u.is_banned && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 uppercase">Activo</span>}
              </div>
              <div className="col-span-12 md:col-span-3 flex justify-end flex-wrap gap-1">
                <IconAction testid={`users-real-edit-${u.id}`}     onClick={() => setEditingUser(u)} title="Editar"          icon={Pencil} />
                <IconAction testid={`users-real-resetpw-${u.id}`}  onClick={() => resetPw(u)}        title="Resetear pwd"   icon={KeyRound} />
                {u.is_banned
                  ? <IconAction testid={`users-real-unban-${u.id}`} onClick={() => unban(u)} title="Reactivar" icon={ShieldCheck} className="text-emerald-700" />
                  : <IconAction testid={`users-real-ban-${u.id}`}   onClick={() => ban(u)}   title="Suspender" icon={Ban}         className="text-amber-700" />
                }
                {u.is_active
                  ? <IconAction testid={`users-real-deact-${u.id}`} onClick={() => deact(u)} title="Desactivar" icon={ShieldOff} />
                  : <IconAction testid={`users-real-act-${u.id}`}   onClick={() => act(u)}   title="Activar"    icon={ShieldCheck} className="text-emerald-700" />
                }
                <IconAction testid={`users-real-delete-${u.id}`}    onClick={() => remove(u)}        title="Eliminar"         icon={Trash2} className="text-red-700" />
              </div>
            </div>
          );
        })}
      </section>

      <div className="flex items-center justify-between text-xs font-mono text-ink-mid">
        <span>Página {currentPage} de {totalPages}</span>
        <div className="flex gap-2">
          <button data-testid="users-real-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="px-3 py-1 rounded-full border hairline disabled:opacity-30">‹ Anterior</button>
          <button data-testid="users-real-next" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="px-3 py-1 rounded-full border hairline disabled:opacity-30">Siguiente ›</button>
        </div>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={(creds) => { setShowCreate(false); setCredToShow(creds); load(); }} />}
      {editingUser && <EditModal user={editingUser} onClose={() => setEditingUser(null)} onSaved={() => { setEditingUser(null); load(); }} />}
      {credToShow && <CredsModal data={credToShow} onClose={() => setCredToShow(null)} />}
    </div>
  );
}

function IconAction({ icon: Icon, onClick, title, testid, className = "" }) {
  return (
    <button data-testid={testid} onClick={onClick} title={title} className={`p-1.5 rounded-full hover:bg-cream-deep transition ${className}`}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function CreateModal({ onClose, onCreated }) {
  const auth = useAdminAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [role, setRole] = useState("user");
  const [password, setPassword] = useState("");
  const [autoPw, setAutoPw] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { email, name, handle: handle || undefined, role };
      if (!autoPw && password) body.password = password;
      const { data } = await auth.apiPost("/admin/users-real", body);
      const credentials = {
        email: data.user.email,
        password: data.generated_password || password,
        mode: "create",
      };
      toast.success("Usuario creado");
      onCreated(credentials);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al crear usuario");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Crear usuario" onClose={onClose} testid="users-real-create-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Nombre completo *" value={name} onChange={setName} testid="users-real-create-name" required />
        <Input label="Email *" type="email" value={email} onChange={setEmail} testid="users-real-create-email" required />
        <Input label="Handle (opcional, ej. @lucia.mx)" value={handle} onChange={setHandle} testid="users-real-create-handle" />
        <Select label="Rol" value={role} onChange={setRole} testid="users-real-create-role" options={ROLES} />
        <label className="flex items-center gap-2 text-xs text-ink-mid">
          <input type="checkbox" checked={autoPw} onChange={(e) => setAutoPw(e.target.checked)} data-testid="users-real-create-autopw" />
          Generar contraseña automáticamente
        </label>
        {!autoPw && <Input label="Contraseña (mín. 8)" type="password" value={password} onChange={setPassword} testid="users-real-create-password" required minLength={8} />}
        <button type="submit" disabled={busy} data-testid="users-real-create-submit" className="w-full bg-coral text-white rounded-full py-2.5 text-sm hover:bg-coral-hover disabled:opacity-50">
          {busy ? "Creando…" : "Crear usuario"}
        </button>
      </form>
    </Modal>
  );
}

function EditModal({ user, onClose, onSaved }) {
  const auth = useAdminAuth();
  const [form, setForm] = useState({
    name: user.name || "",
    handle: user.handle || "",
    bio: user.bio || "",
    role: user.role || "user",
    is_pro: !!user.is_pro,
    is_verified: !!user.is_verified,
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await auth.apiPatch(`/admin/users-real/${user.id}`, form);
      toast.success("Cambios guardados");
      onSaved();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Editar · ${user.email}`} onClose={onClose} testid="users-real-edit-modal">
      <form onSubmit={save} className="space-y-3">
        <Input label="Nombre" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
        <Input label="Handle" value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} testid="edit-handle" />
        <Input label="Bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} testid="edit-bio" />
        <Select label="Rol" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="edit-role" options={ROLES} />
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_pro} onChange={(e) => setForm({ ...form, is_pro: e.target.checked })} data-testid="edit-is-pro" /> Marca como PRO
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_verified} onChange={(e) => setForm({ ...form, is_verified: e.target.checked })} data-testid="edit-is-verified" /> Verificado
          </label>
        </div>
        <button type="submit" disabled={busy} data-testid="edit-submit" className="w-full bg-coral text-white rounded-full py-2.5 text-sm hover:bg-coral-hover disabled:opacity-50">
          {busy ? "Guardando…" : "Guardar cambios"}
        </button>
      </form>
    </Modal>
  );
}

function CredsModal({ data, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(`${data.email}\n${data.password}`);
    toast.success("Copiado al portapapeles");
  };
  return (
    <Modal title={data.mode === "reset" ? "Contraseña restablecida" : "Usuario creado"} onClose={onClose} testid="users-real-creds-modal">
      <div className="space-y-3">
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">
          ⚠ Esta es la única vez que verás esta contraseña. Cópiala y compártela de forma segura con el usuario.
        </div>
        <div className="bg-cream rounded-xl p-3 border hairline space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-wider text-ink-mid">Email</p>
          <p className="font-mono text-sm break-all">{data.email}</p>
          <p className="mt-2 text-[10px] font-mono uppercase tracking-wider text-ink-mid">Contraseña</p>
          <p data-testid="creds-modal-password" className="font-mono text-sm break-all">{data.password}</p>
        </div>
        <button onClick={copy} data-testid="creds-modal-copy" className="w-full inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full py-2.5 text-sm hover:bg-coral">
          <Copy className="w-3.5 h-3.5" /> Copiar credenciales
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, testid, children }) {
  return (
    <div onClick={onClose} className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} data-testid={testid} className="bg-cream-card border hairline rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b hairline">
          <h3 className="font-serif text-xl tracking-tight">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-cream-deep flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, testid, type = "text", value, onChange, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        {...rest}
        className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral"
      />
    </label>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral"
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}
